import { computed } from 'vue'
import { RouteLocationRaw, useRouter } from 'vue-router'

import PGFaviconSVG from '@/assets/icons/favicon.svg?file'
import { AvailableLocale, mapLocaleToHrefLang } from '@/i18n/helpers'
import { useI18nAddon } from '@/i18n/i18n'
import { ROUTE_NAMES } from '@/router/constants'
import { RAW_SOCIAL_LINKS } from '@/router/navigation-constants'

import { useConfig } from './config'
import { useTr } from './use-tr'

export const SCHEMA_CURRENCY = 'USD'
export const SCHEMA_WORST_RATING = 1
export const SCHEMA_BEST_RATING = 5
const ORGANIZATION_VALUES = {
    vatID: '104378095400003',
    propertyID: 'DUNS',
    value: '850036975',
}

export function useSchemaOrg() {
    const router = useRouter()
    const tr = useTr()
    const { currentLocale } = useI18nAddon()
    const { VUE_APP_ANDROID_APP_URL } = useConfig()
    const mainUrl = computed(() => getFullLink({ name: ROUTE_NAMES.MAIN }))
    const WebSiteId = computed(() => `${mainUrl.value}#website`)

    function getFullLink(href: string | RouteLocationRaw) {
        if (typeof href === 'string') {
            return `https://playgama.com${href}`
        } else {
            return `https://playgama.com${router.resolve(href).fullPath}`
        }
    }

    function getInLanguage(locale?: AvailableLocale) {
        return mapLocaleToHrefLang(locale || currentLocale.value)
    }
    // Do not translate publisher - in uses for non-locale and locale pages as well
    const Organization = computed(() => ({
        '@type': 'Organization',
        '@id': `https://playgama.com/#organization`,
        name: 'Playgama',
        legalName: 'LeadToChanges L.L.C.-FZ',
        alternateName: 'Playgama',
        url: 'https://playgama.com',
        brand: {
            '@type': 'Brand',
            '@id': 'https://playgama.com/#brand',
            name: 'Playgama',
            slogan: 'Play, Share, Profit. Game on!',
        },
        image: PGFaviconSVG,
        logo: {
            '@type': 'ImageObject',
            url: PGFaviconSVG,
        },
        email: 'support@playgama.com',
        vatID: ORGANIZATION_VALUES.vatID,
        identifier: {
            '@type': 'PropertyValue',
            propertyID: ORGANIZATION_VALUES.propertyID,
            value: ORGANIZATION_VALUES.value,
        },
        sameAs: [...RAW_SOCIAL_LINKS, VUE_APP_ANDROID_APP_URL],
        address: {
            '@type': 'PostalAddress',
            postalCode: '500001',
            addressLocality: 'Dubai',
            streetAddress: 'Nad Al Sheba',
            addressCountry: {
                '@type': 'Country',
                name: 'AE',
            },
        },
        contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'User Feedback',
            url: getFullLink({ name: ROUTE_NAMES.CONTACTS }),
            email: 'support@playgama.com',
        },
        founder: [
            {
                '@type': 'Person',
                name: 'Dmitry Kachmar',
                jobTitle: 'Founder',
                sameAs: ['https://www.linkedin.com/in/dmitry-kachmar-84392725/'],
            },
        ],
        description:
            'Playgama is a fast-growing platform that connects 4,000+ HTML5 game developers with a 450M+ monthly network reach. Whether you’re looking to publish, monetize, or scale your game, Playgama gives you the tools and exposure to make it happen.',
    }))

    const WebSite = computed(() => ({
        '@type': 'WebSite',
        '@id': WebSiteId.value,
        name: 'Playgama',
        url: mainUrl.value,
        description: tr('headMeta.description'),
        inLanguage: getInLanguage(),
        hasPart: [
            {
                '@type': 'MobileApplication',
                '@id': `${VUE_APP_ANDROID_APP_URL}#mobileapp`,
                downloadUrl: VUE_APP_ANDROID_APP_URL,
                installUrl: VUE_APP_ANDROID_APP_URL,
                name: 'Playgama',
                applicationCategory: 'GameApplication',
                operatingSystem: 'Android',
                offers: {
                    '@type': 'Offer',
                    price: '0',
                    priceCurrency: 'USD',
                },
            },
        ],
        publisher: {
            '@id': 'https://playgama.com/#organization',
        },
    }))

    return {
        WebSite,
        WebSiteId,
        Organization,
        getFullLink,
        getInLanguage,
    }
}
